/**
 * Header 컴포넌트 - 사이트 상단 헤더
 * 스크롤에 따른 배경 투명도 변화 애니메이션 포함
 */
// import { Link } from 'react-router-dom'; // 웹빌더에서는 라우터 미사용
import { useEffect } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import { Gnb } from './Gnb';
import { Menu } from './Menu';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface HeaderProps {
    logoText?: string;
    logoUrl?: string;
    menus?: any[];
    isLoggedIn?: boolean;
    isBusiness?: boolean;
    companyId?: number;
}

export function Header({ logoText = 'Marijour', logoUrl = '', menus = [], isLoggedIn = false, isBusiness = false, companyId }: HeaderProps) {
    useGSAP(() => {
        const header = document.querySelector('#header');
        const logo = document.querySelector('#header .logo');
        if (header && logo) {
            // GSAP 존재 여부 체크
            if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
                // 로드 시 fade-in 애니메이션
                gsap.to(logo, {
                    opacity: 1,
                    duration: 0.9,
                    ease: 'power2.out',
                });

                // 스크롤 시 배경 투명도 전환
                ScrollTrigger.create({
                    trigger: header,
                    start: 'top top',
                    end: 'bottom top',
                    onEnter: () => gsap.to(header, { backgroundColor: 'rgba(255, 255, 255, 0.95)', duration: 0.5 }),
                    onEnterBack: () => gsap.to(header, { backgroundColor: 'rgba(255, 255, 255, 1)', duration: 0.5 }),
                });
            } else {
                // GSAP이 없을 때는 직접 opacity 설정
                (logo as HTMLElement).style.opacity = '1';
            }
        }
    }, []);

    // GSAP이 없을 때를 위한 대체 스크롤 이벤트
    useEffect(() => {
        const handleScroll = () => {
            const header = document.querySelector('#header');
            if (header) {
                if (window.scrollY > 50) {
                    header.classList.add('scrolled');
                } else {
                    header.classList.remove('scrolled');
                }
            }
        };

        // GSAP이 없으면 일반 스크롤 이벤트 사용
        if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
            window.addEventListener('scroll', handleScroll);
            handleScroll(); // 초기 상태 확인
            
            return () => {
                window.removeEventListener('scroll', handleScroll);
            };
        }
    }, []);

    return (
        <header
            id="header"
            className="sticky top-0 bg-white z-[100001]"
        >
            <div className="globalWrapper flex items-center justify-between">
                    <Gnb menus={menus} isLoggedIn={isLoggedIn} isBusiness={isBusiness} />
                    <div className="logo opacity-0">
                        <h1 className="text-[23px] font-serif leading-[1]">
                            <a href="/" className="flex items-center">
                                {logoUrl ? (
                                    <img 
                                        src={logoUrl} 
                                        alt={logoText} 
                                        className="max-h-[40px] lg:max-h-[45px] w-auto object-contain"
                                        style={{
                                            maxWidth: companyId === 22 ? "150px" : "200px"
                                        }}
                                        onError={(e) => {
                                            console.error("로고 이미지 로드 오류");
                                            // 이미지 로드 실패 시 텍스트로 표시
                                            const target = e.target as HTMLImageElement;
                                            target.style.display = 'none';
                                            const textSpan = document.createElement('span');
                                            textSpan.textContent = logoText;
                                            target.parentElement?.appendChild(textSpan);
                                        }}
                                    />
                                ) : (
                                    logoText
                                )}
                            </a>
                        </h1>
                    </div>
                    <Menu isLoggedIn={isLoggedIn} />
            </div>
            {/* 메가메뉴 컨테이너 - 헤더 영역 내부에 위치 */}
            <div id="megaMenuContainer"></div>
        </header>
    );
}
